/**
 * ReportsDrillView.tsx — the ONE filtered-deal drill view (§7.B) and the
 * ONE reconciliation path it renders (reportsDrill.ts). SalesReports' own
 * drills, the SB-12 dashboard drills and, since §33, the Forecast grid's
 * won-cell drill all render through THIS component; there is deliberately
 * no second drill component. `backLabel` names the hosting page.
 *
 * RELOCATED from SalesReports.tsx under §33 W-5 — a move, not a fork
 * (SB-12's own instruction: "extract ReportsDrillView to its own file —
 * relocate, don't fork"). Before the move SalesReports ⇄ SalesDashboard
 * imported each other (SalesMetric one way, this view the other), and a
 * SalesForecast import of the view would have added SalesForecast ⇄
 * SalesReports as a third render-time cycle. Now every page imports the
 * view from here and this file imports no page: the cycles are gone.
 * `drillTitle` and `OPEN_SLUGS` moved with it, byte for byte.
 */
import { Avatar, DealStageBadge, LoadingState, ErrorState, EmptyState } from '../../DashboardShared'
import { useAsync, fetchDeals, fetchClosedDeals } from '../../api'
import { money, nonGbpNote } from '../../../../lib/money'
import { monthLabel } from './revenueByMonthView'
import { periodEndInclusive, periodLabel, periodRangeLabel } from './salesPeriod'
import {
  type ReportsDrill, filterMonthDeals, drillShortfall, weightedRowPence, cycleDays, cycleRows, meanCycleDays,
  cycleMeanDiffers, nonGbpExcludedCount,
} from './reportsDrill'

// ── The drill view (§7.B): shared filtered deal table, Archive layout ─────────

// The 4 open-stage slugs in funnel order — the composite 'open' drill's fan-out
// ("open" has no server filter; ListDealsQuery is stage/assigned_to only).
const OPEN_SLUGS = ['new_lead', 'qualified', 'proposal_sent', 'negotiation']

function drillTitle(drill: ReportsDrill): { title: string; subtitle: string } {
  switch (drill.kind) {
    case 'stage':
      return { title: drill.label, subtitle: `${drill.count} ${drill.count === 1 ? 'deal' : 'deals'} in this stage` }
    case 'rep':
      return {
        title: drill.rep,
        subtitle: `${drill.openDeals} open · ${money(drill.pipelinePence / 100)} pipeline · ${money(drill.wonThisQuarterPence / 100)} won this quarter (since ${drill.quarterStart})`,
      }
    case 'month':
      return { title: `${monthLabel(drill.month)} ${drill.month.slice(0, 4)}`, subtitle: `${money(drill.wonPence / 100)} closed-won value` }
    case 'open':
      // D-3: the Avg. deal size card's variant restates the mean — the SAME
      // population, one more sentence; the figures are the server's.
      return drill.avgPence != null
        ? { title: 'Open deals', subtitle: `${money(drill.pipelinePence / 100)} across ${drill.openDeals} open ${drill.openDeals === 1 ? 'deal' : 'deals'} · mean ${money(drill.avgPence / 100)}` }
        : { title: 'Open deals', subtitle: `${drill.openDeals} open · ${money(drill.pipelinePence / 100)} total pipeline` }
    case 'weighted':
      return { title: 'Weighted pipeline', subtitle: `${money(drill.weightedPence / 100)} weighted of ${money(drill.pipelinePence / 100)} across ${drill.openDeals} open ${drill.openDeals === 1 ? 'deal' : 'deals'}` }
    case 'cycle':
      return { title: 'Avg. sales cycle', subtitle: `${drill.wonCount} won ${drill.wonCount === 1 ? 'deal' : 'deals'} · mean ${drill.meanDays.toFixed(1)} days, created → closed` }
    case 'closed': {
      const closed = drill.wonCount + drill.lostCount
      const pct = closed > 0 ? Math.round((drill.wonCount / closed) * 100) : 0
      return { title: 'Closed deals', subtitle: `${drill.wonCount} won · ${drill.lostCount} lost · ${pct}% win rate` }
    }
    case 'forecastWon': {
      // §33 W-2: the cell's own figures and the period the RESPONSE named —
      // the same words the grid's subtitle uses, so the reader is told which
      // population this is (linked employee, won INSIDE the window; §26 F-2).
      const period = { kind: drill.periodKind, start: drill.periodStart }
      const excluded = drill.wonExcludedNonGbp > 0 ? ` (${drill.wonExcludedNonGbp} excluded)` : ''
      return {
        title: drill.employeeName,
        subtitle: `${money(drill.wonPence / 100)} across ${drill.wonCount} ${drill.wonCount === 1 ? 'deal' : 'deals'}${excluded} won in ${periodLabel(period)} · ${periodRangeLabel(period)}`,
      }
    }
  }
}

/** Shared filtered-deal drill (§7.B) — SalesReports' own drills AND the SB-12
 *  dashboard drills render through THIS view; there is deliberately no second
 *  drill component or reconciliation path. `backLabel` names the hosting page. */
export function ReportsDrillView({ drill, onBack, onSelectDeal, backLabel = 'Back to reports' }: {
  drill: ReportsDrill
  onBack: () => void
  onSelectDeal?: (id: string) => void
  backLabel?: string
}) {
  const { data, loading, error } = useAsync(
    () =>
      drill.kind === 'stage' ? fetchDeals({ stage: drill.stage })
      : drill.kind === 'rep' ? fetchDeals(drill.employeeId
          ? { assignedEmployeeId: drill.employeeId }   // linked owner
          : { assignedTo: drill.rep })                  // the unlinked bucket
      : drill.kind === 'open' || drill.kind === 'weighted' ? Promise.all(OPEN_SLUGS.map(s => fetchDeals({ stage: s }))).then(r => r.flat())
      : drill.kind === 'closed' ? fetchClosedDeals()
      : drill.kind === 'cycle' ? fetchDeals({ stage: 'closed_won' })
      // §33 W-2/W-3: the server's own predicate for the won cell (stage,
      // linked employee, close window). closedTo is the period's LAST day —
      // the server reads that bound as a whole day, and the response's
      // periodEnd is EXCLUSIVE; sending it would admit the next period's
      // first day. Pinned on the request (SalesForecast.test), because on a
      // tenant with no boundary-day close both bounds return the same rows.
      : drill.kind === 'forecastWon' ? fetchDeals({
          stage: 'closed_won', assignedEmployeeId: drill.employeeId,
          closedFrom: drill.periodStart, closedTo: periodEndInclusive({ kind: drill.periodKind, start: drill.periodStart }),
        })
      : fetchDeals({ stage: 'closed_won' }).then(ds => filterMonthDeals(ds, drill.month)),
    [drill],
  )

  const { title, subtitle } = drillTitle(drill)

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const deals = data ?? []
  const shortfall = drillShortfall(drill, deals)
  // §23 D-5: the cycle drill lists only the rows it can measure; the rest are
  // COUNTED (the gate above judged every fetched row against wonCount).
  const cycle = drill.kind === 'cycle' ? cycleRows(deals) : null
  const listed = cycle ? cycle.listed : deals
  const clientMean = cycle ? meanCycleDays(cycle.listed) : null
  // §23 D-9: the rows the clicked £ figure excluded, derived from the rows.
  const excludedNonGbp = nonGbpExcludedCount(listed)
  // The two kind-specific columns — never on the shared table (D-12).
  const weightedCol = drill.kind === 'weighted'
  const daysCol = drill.kind === 'cycle'

  return (
    <div className="page-enter flex flex-col gap-4">
      <div>
        <button type="button" onClick={onBack}
          className="text-12 text-accent font-medium hover:underline focus:outline-none rounded-control">
          ← {backLabel}
        </button>
      </div>

      <div className="k-card">
        <div className="k-card-head">
          <div>
            <div className="k-card-title">{title}</div>
            <div className="k-card-sub">{subtitle}</div>
          </div>
        </div>
        {shortfall && (
          <div className="px-5 py-2 text-12 text-ink-muted border-b border-edge-row">
            {shortfall.unit === 'deals' && `Showing ${shortfall.shown} of ${shortfall.expected} deals — list calls cap at 200.`}
            {shortfall.unit === 'pence' && `Showing ${money(shortfall.shown / 100)} of ${money(shortfall.expected / 100)} — list calls cap at 200.`}
            {shortfall.unit === 'rep' && `This list doesn't fully reconcile to the row's figures — list calls cap at 200.`}
          </div>
        )}
        {cycle && cycle.dropped > 0 && (
          <div className="px-5 py-2 text-12 text-ink-muted border-b border-edge-row">
            {`${cycle.dropped} of ${deals.length} won ${deals.length === 1 ? 'deal has' : 'deals have'} no close stamp and ${cycle.dropped === 1 ? 'is' : 'are'} not listed.`}
          </div>
        )}
        {cycle && drill.kind === 'cycle' && cycle.listed.length > 0 && cycleMeanDiffers(clientMean, drill.meanDays) && (
          // D-5: the recomputed mean disagrees with the server's at 0.1-day
          // precision — the cap, or dropped rows — and that is SAID.
          <div className="px-5 py-2 text-12 text-ink-muted border-b border-edge-row">
            {`Mean over the ${cycle.listed.length} listed ${cycle.listed.length === 1 ? 'row' : 'rows'}: ${clientMean!.toFixed(1)} days — the server's figure covers rows this list does not.`}
          </div>
        )}
        {excludedNonGbp > 0 && (
          // D-9: an uncaptured non-GBP row is SHOWN in the list but is not in
          // the £ figure that was clicked — the nonGbpNote idiom says so.
          <div className="px-5 py-2 text-12 text-ink-muted border-b border-edge-row">
            {`${nonGbpNote(excludedNonGbp, 'deal')} in the £ figure`}
          </div>
        )}
        {listed.length === 0 ? (
          <EmptyState message="No deals behind this number" hint="Nothing matches this drill right now." />
        ) : (
          <div className="overflow-x-auto">
            <table className="closr-table sales-table w-full">
              <thead>
                <tr>
                  <th className="text-left">Deal</th>
                  <th className="text-left">Company</th>
                  <th className="text-left">Owner</th>
                  <th className="text-left">Value</th>
                  {weightedCol && <th className="text-left">Weighted</th>}
                  <th className="text-left">Stage</th>
                  <th className="text-left">Closed</th>
                  {daysCol && <th className="text-left">Days</th>}
                </tr>
              </thead>
              <tbody>
                {listed.map(d => (
                  <tr key={d.id} className="cursor-pointer hover:bg-surface-row-hover transition-colors"
                    onClick={() => onSelectDeal?.(d.id)}>
                    <td className="text-13 font-semibold text-ink-primary">{d.name}</td>
                    <td>
                      <div className="flex items-center gap-2 text-13 text-ink-body">
                        <Avatar name={d.owner} size={22} />{d.company}
                      </div>
                    </td>
                    <td className="text-13 text-ink-body">{d.owner}</td>
                    <td className="text-13 font-semibold text-ink-primary">{money(d.amount / 100, d.currency)}</td>
                    {weightedCol && (
                      // D-4: ⌊fenced × effective ÷ 100⌋ per row; a row outside the
                      // fence has no weighted figure and says so with a dash.
                      <td className="text-13 text-ink-body">{(() => { const w = weightedRowPence(d); return w == null ? '—' : `${money(w / 100)} · ${d.effectiveProbability}%` })()}</td>
                    )}
                    <td><DealStageBadge stage={d.stage} /></td>
                    <td className="text-12 text-ink-secondary">{d.closedDate || '—'}</td>
                    {daysCol && (
                      // D-5: whole days, floored, from the two stamps.
                      <td className="text-13 text-ink-body">{(() => { const v = cycleDays(d); return v == null ? '—' : String(Math.floor(v)) })()}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
